import { create } from "zustand";
import { auth } from '../../../../auth';
import convex, { waitForConvexAuthReady } from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import { withActiveTenant } from "../../../../utils/tenantUtils";
import { getBrowserTimeZone } from "../../../../utils/timeZone";

const normalizeEvent = (doc) => ({
  id: doc._id || doc.id,
  title: doc?.title ?? "",
  description: doc?.description ?? "",
  location: doc?.location ?? "",
  startDate: doc?.startDate ?? "",
  endDate: doc?.endDate ?? "",
  startTime: doc?.startTime ?? "",
  endTime: doc?.endTime ?? "",
  allDay: doc?.allDay ?? false,
  category: doc?.category ?? "Other",
  color: doc?.color ?? "#000060",
  reminders: Array.isArray(doc?.reminders) ? doc.reminders : [],
  attendees: Array.isArray(doc?.attendees) ? doc.attendees : [],
  recurrence: doc?.recurrence ?? null,
  // Recurring event fields (Phase 6)
  recurrenceId: doc?.recurrenceId ?? null,
  originalDate: doc?.originalDate ?? null,
  exDates: Array.isArray(doc?.exDates) ? doc.exDates : [],
  userId: doc?.userId,
  metadata: {
    userId: doc?.metadata?.userId,
    createdAt: doc?.metadata?.createdAt ?? null,
    updatedAt: doc?.metadata?.updatedAt ?? null,
  },
  sharedWith: Array.isArray(doc?.sharedWith) ? doc.sharedWith : [],
  taskId: doc?.taskId ?? null,
  ownerType: doc?.ownerType ?? "personal",
  groupId: doc?.groupId ?? null,
  timeZone: doc?.timeZone ?? "UTC",
});

/** Re-fetch all events and update the store. */
async function refetchEvents(set) {
  await waitForConvexAuthReady();
  const docs = await convex.query(api.events.listEvents, withActiveTenant());
  set({ events: docs.map(normalizeEvent), loading: false });
}

const useEventStore = create((set, get) => ({
  events: [],
  loading: false,
  error: null,

  fetchEvents: async () => {
    const user = auth.currentUser;
    if (!user) { set({ events: [], error: "Not authenticated" }); return; }
    set({ loading: true, error: null });
    try {
      await refetchEvents(set);
    } catch (e) {
      set({ loading: false, error: e.message || "Failed to fetch events" });
    }
  },

  createEvent: async (payload) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const id = await convex.mutation(api.events.createEvent, withActiveTenant({
      title: payload.title?.trim() ?? "",
      description: payload.description ?? "",
      location: payload.location ?? "",
      startDate: payload.startDate ?? "",
      endDate: payload.endDate ?? payload.startDate ?? "",
      startTime: payload.startTime ?? "",
      endTime: payload.endTime ?? "",
      allDay: payload.allDay ?? false,
      category: payload.category ?? "Other",
      color: payload.color ?? "#000060",
      reminders: payload.reminders || [],
      attendees: payload.attendees || [],
      recurrence: payload.recurrence || null,
      timeZone: payload.timeZone || getBrowserTimeZone(),
      sharedWith: Array.isArray(payload.sharedWith) ? payload.sharedWith : [],
      ownerType: payload.ownerType ?? "personal",
      groupId: payload.groupId ?? undefined,
      ...(payload.taskId ? { taskId: payload.taskId } : {}),
    }));

    // Refetch to include the new event (and its recurrence state) in one pass
    await refetchEvents(set);
    return id;
  },

  /**
   * Update a non-recurring event, or update a recurring event with scope="all".
   * For scoped edits ("this" / "thisAndFollowing"), use updateRecurringEvent.
   */
  updateEvent: async (id, updates) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    await convex.mutation(api.events.updateEvent, { id, ...updates });
    await refetchEvents(set);
  },

  /**
   * Scoped update for recurring event occurrences.
   *
   * @param {string} masterId      — Convex ID of the master event
   * @param {object} updates       — field updates to apply
   * @param {"this"|"thisAndFollowing"|"all"} scope
   * @param {string} [originalDate] — "YYYY-MM-DD" of the occurrence (required for "this" / "thisAndFollowing")
   */
  updateRecurringEvent: async (masterId, updates, scope, originalDate) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    await convex.mutation(api.events.updateEvent, {
      id: masterId,
      scope,
      originalDate,
      ...updates,
    });
    await refetchEvents(set);
  },

  /**
   * Delete an event.
   *
   * @param {string} id              — event Convex ID
   * @param {string} [scope]         — "this" | "thisAndFollowing" | "all" (omit for non-recurring)
   * @param {string} [originalDate]  — "YYYY-MM-DD" of the occurrence (for scoped deletes)
   */
  deleteEvent: async (id, scope = null, originalDate = null) => {
    const args = { id };
    if (scope) args.scope = scope;
    if (originalDate) args.originalDate = originalDate;
    await convex.mutation(api.events.deleteEvent, args);
    await refetchEvents(set);
  },
}));

export default useEventStore;
